import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Card, Chip, Checkbox, Collapse, Button, IconButton
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    ArrowForward as ArrowForwardIcon,
    Bolt as BoltIcon,
    Compare as CompareIcon,
} from '@mui/icons-material';

import { accentColor, getTopologyColor, MetricRow } from './utils/topologyColors';
import { vortxTypography } from '@vortx/shared-ui';
import { getTopologies } from '@vortx/shared-ui';
import { Skeleton } from './Skeleton';
import { useSkeletonState } from '../../hooks/useSkeletonState';

const TopologyRow = ({
    topology,
    isExpanded,
    isSelected,
    onToggleExpand,
    onSelect,
    onContinue,
    compareMode,
    selectedCount,
}) => {
    const color = getTopologyColor(topology.id);

    return (
        <Card
            sx={{
                bgcolor: isSelected ? alpha(color, 0.15) : 'rgba(30, 30, 30, 0.8)',
                border: `1px solid ${isSelected ? color : 'rgba(255,255,255,0.1)'}`,
                transition: 'all 0.2s ease',
                mb: 1,
                overflow: 'hidden',
                '&:hover': {
                    borderColor: alpha(color, 0.5),
                },
            }}
        >
            {/* Row Header - Clickable */}
            <Box
                onClick={() => onToggleExpand(topology.id)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 1.5,
                    cursor: 'pointer',
                    '&:hover': {
                        bgcolor: alpha(color, 0.05),
                    },
                }}
            >
                {/* Compare Mode Checkbox */}
                {compareMode && (
                    <Checkbox
                        checked={isSelected}
                        onChange={(e) => {
                            e.stopPropagation();
                            onSelect(topology);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        disabled={!isSelected && selectedCount >= 2}
                        sx={{
                            color: color,
                            '&.Mui-checked': { color },
                            '&.Mui-disabled': { color: 'rgba(255,255,255,0.2)' },
                            p: 0.5,
                            mr: 1,
                        }}
                    />
                )}

                {/* Topology Number Badge */}
                <Box sx={{
                    bgcolor: alpha(color, 0.2),
                    color: color,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    minWidth: 36,
                    textAlign: 'center',
                    mr: 2,
                }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.75rem',
                        fontWeight: 700,
                    }}>
                        #{topology.id}
                    </Typography>
                </Box>

                {/* Topology Name & Use Case */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.heading,
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        color: '#f4f1ec',
                        mb: 0.25,
                    }}>
                        {topology.name}
                    </Typography>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.7rem',
                        color: '#888',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}>
                        {topology.use_case}
                    </Typography>
                </Box>

                {/* Quick Stats */}
                <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
                    {topology.pv_to_ev_efficiency > 0 && (
                        <Chip
                            label={`${(topology.pv_to_ev_efficiency * 100).toFixed(0)}%`}
                            size="small"
                            sx={{
                                height: 22,
                                fontSize: '0.65rem',
                                fontFamily: vortxTypography.mono,
                                fontWeight: 600,
                                bgcolor: alpha('#2ecc71', 0.2),
                                color: '#2ecc71',
                            }}
                        />
                    )}
                    {topology.dc_bus_voltage > 0 && (
                        <Chip
                            label={`${topology.dc_bus_voltage}V`}
                            size="small"
                            sx={{
                                height: 22,
                                fontSize: '0.65rem',
                                fontFamily: vortxTypography.mono,
                                bgcolor: alpha('#5fd4c4', 0.2),
                                color: '#5fd4c4',
                            }}
                        />
                    )}
                </Box>

                {/* Selected Badge */}
                {isSelected && (
                    <Chip
                        label={compareMode ? "COMPARING" : "SELECTED"}
                        size="small"
                        sx={{
                            height: 22,
                            fontSize: '0.6rem',
                            fontFamily: vortxTypography.mono,
                            fontWeight: 700,
                            bgcolor: color,
                            color: '#000',
                            mr: 1,
                        }}
                    />
                )}

                <IconButton size="small" sx={{ color: '#888' }}>
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </Box>

            {/* Expanded Details */}
            <Collapse in={isExpanded}>
                <Box sx={{
                    px: 2,
                    pb: 2,
                    pt: 1,
                    borderTop: '1px solid rgba(255,255,255,0.05)',
                    bgcolor: 'rgba(0,0,0,0.2)',
                }}>
                    {/* Description */}
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.75rem',
                        color: '#aaa',
                        mb: 2,
                        lineHeight: 1.6,
                    }}>
                        {topology.description}
                    </Typography>

                    {/* Two Column Layout for Metrics */}
                    <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                        {/* Efficiency Column */}
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.6rem',
                                color: '#666',
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                                mb: 1,
                            }}>
                                Efficiency Ratings
                            </Typography>
                            <MetricRow
                                label="PV → EV"
                                value={topology.pv_to_ev_efficiency > 0
                                    ? `${(topology.pv_to_ev_efficiency * 100).toFixed(1)}%`
                                    : 'N/A'}
                                color="#2ecc71"
                            />
                            <MetricRow
                                label="PV → BESS"
                                value={topology.pv_to_bess_efficiency > 0
                                    ? `${(topology.pv_to_bess_efficiency * 100).toFixed(1)}%`
                                    : 'N/A'}
                                color="#5fd4c4"
                            />
                            <MetricRow
                                label="Grid → EV"
                                value={topology.grid_to_ev_efficiency
                                    ? `${(topology.grid_to_ev_efficiency * 100).toFixed(1)}%`
                                    : 'N/A'}
                                color="#f39c12"
                            />
                        </Box>

                        {/* Architecture Column */}
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.6rem',
                                color: '#666',
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                                mb: 1,
                            }}>
                                Architecture
                            </Typography>
                            {topology.dc_bus_voltage > 0 && (
                                <MetricRow label="DC Bus" value={`${topology.dc_bus_voltage}V`} />
                            )}
                            {topology.ac_bus_voltage > 0 && (
                                <MetricRow label="AC Bus" value={`${topology.ac_bus_voltage}V`} />
                            )}
                            <MetricRow
                                label="Conversions PV→EV"
                                value={topology.pv_to_ev_conversions || 'N/A'}
                            />
                        </Box>
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {compareMode ? (
                            <Button
                                variant={isSelected ? "outlined" : "contained"}
                                onClick={() => onSelect(topology)}
                                disabled={!isSelected && selectedCount >= 2}
                                sx={{
                                    flex: 1,
                                    borderColor: color,
                                    bgcolor: isSelected ? 'transparent' : color,
                                    color: isSelected ? color : '#000',
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.75rem',
                                    py: 1,
                                    '&:hover': {
                                        borderColor: color,
                                        bgcolor: isSelected ? alpha(color, 0.1) : alpha(color, 0.8),
                                    },
                                    '&:disabled': {
                                        bgcolor: 'rgba(255,255,255,0.1)',
                                        color: 'rgba(255,255,255,0.3)',
                                    },
                                }}
                            >
                                {isSelected ? 'Remove from Comparison' : (selectedCount >= 2 ? 'Max 2 Selected' : 'Add to Comparison')}
                            </Button>
                        ) : (
                            <>
                                {!isSelected && (
                                    <Button
                                        variant="outlined"
                                        onClick={() => onSelect(topology)}
                                        sx={{
                                            flex: 1,
                                            borderColor: color,
                                            color: color,
                                            fontFamily: vortxTypography.mono,
                                            fontSize: '0.75rem',
                                            py: 1,
                                            '&:hover': {
                                                borderColor: color,
                                                bgcolor: alpha(color, 0.1),
                                            },
                                        }}
                                    >
                                        Select This Topology
                                    </Button>
                                )}
                                <Button
                                    variant="contained"
                                    endIcon={<ArrowForwardIcon />}
                                    onClick={() => {
                                        if (!isSelected) onSelect(topology);
                                        onContinue(topology);
                                    }}
                                    sx={{
                                        flex: isSelected ? 1 : 1,
                                        bgcolor: color,
                                        color: '#000',
                                        fontFamily: vortxTypography.mono,
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        py: 1,
                                        '&:hover': {
                                            bgcolor: alpha(color, 0.8),
                                            boxShadow: `0 0 20px ${alpha(color, 0.5)}`,
                                        },
                                    }}
                                >
                                    {isSelected ? 'Continue to Simulation' : 'Select & Continue'}
                                </Button>
                            </>
                        )}
                    </Box>
                </Box>
            </Collapse>
        </Card>
    );
};

const TopologyBrowser = ({
    selectedTopologies = [],
    onTopologySelect,
    onContinueToSimulation,
    compareMode = false,
    onToggleCompareMode,
}) => {
    const [topologies, setTopologies] = useState([]);
    const [loading, setLoading] = useState(true);
    const { showSkeleton } = useSkeletonState({ isLoading: loading, minDisplayMs: 300 });
    const [error, setError] = useState(null);
    const [expandedId, setExpandedId] = useState(null);

    useEffect(() => {
        const fetchTopologies = async () => {
            try {
                const data = await getTopologies();
                setTopologies(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchTopologies();
    }, []);

    // Auto-expand first selected topology
    useEffect(() => {
        if (selectedTopologies.length > 0 && !expandedId) {
            setExpandedId(selectedTopologies[0].id);
        }
    }, [selectedTopologies]);

    const handleToggleExpand = (topologyId) => {
        setExpandedId(expandedId === topologyId ? null : topologyId);
    };

    const handleTopologySelect = (topology) => {
        if (compareMode) {
            // Toggle selection in compare mode
            const isSelected = selectedTopologies.some(t => t.id === topology.id);
            if (isSelected) {
                onTopologySelect(selectedTopologies.filter(t => t.id !== topology.id));
            } else if (selectedTopologies.length < 2) {
                onTopologySelect([...selectedTopologies, topology]);
            }
        } else {
            // Single selection mode
            onTopologySelect([topology]);
        }
    };

    if (showSkeleton) {
        return (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <BoltIcon sx={{ color: accentColor, fontSize: 28 }} />
                    <Typography sx={{
                        fontFamily: vortxTypography.heading,
                        fontSize: '1.5rem',
                        fontWeight: 600,
                        color: accentColor,
                    }}>
                        Topology Presets
                    </Typography>
                </Box>
                <Box sx={{ flex: 1, overflow: 'hidden', pr: 1 }}>
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                        <Card key={i} sx={{
                            bgcolor: 'rgba(30, 30, 30, 0.8)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            mb: 1,
                            p: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            animation: `fadeIn 0.3s ease-out ${i * 0.05}s both`,
                        }}>
                            <Skeleton variant="rectangular" width={36} height={28} sx={{ borderRadius: 1, bgcolor: 'rgba(155,89,182,0.15)' }} />
                            <Box sx={{ flex: 1 }}>
                                <Skeleton variant="text" width="60%" sx={{ bgcolor: 'rgba(255,255,255,0.08)', height: 18 }} />
                                <Skeleton variant="text" width="40%" sx={{ bgcolor: 'rgba(255,255,255,0.05)', height: 14, mt: 0.5 }} />
                            </Box>
                            <Skeleton variant="rectangular" width={50} height={22} sx={{ borderRadius: 10, bgcolor: 'rgba(155,89,182,0.1)' }} />
                        </Card>
                    ))}
                </Box>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography sx={{ color: '#e74c3c', fontFamily: vortxTypography.mono }}>
                    Error: {error}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <BoltIcon sx={{ color: accentColor, fontSize: 28 }} />
                    <Typography sx={{
                        fontFamily: vortxTypography.heading,
                        fontSize: '1.5rem',
                        fontWeight: 600,
                        color: accentColor,
                        textShadow: `0 0 20px ${alpha(accentColor, 0.5)}`,
                    }}>
                        Topology Presets
                    </Typography>
                </Box>

                {/* Compare Mode Toggle */}
                <Button
                    variant={compareMode ? "contained" : "outlined"}
                    startIcon={<CompareIcon />}
                    onClick={onToggleCompareMode}
                    size="small"
                    sx={{
                        borderColor: accentColor,
                        bgcolor: compareMode ? accentColor : 'transparent',
                        color: compareMode ? '#000' : accentColor,
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.7rem',
                        '&:hover': {
                            borderColor: accentColor,
                            bgcolor: compareMode ? alpha(accentColor, 0.8) : alpha(accentColor, 0.1),
                        },
                    }}
                >
                    {compareMode ? 'Exit Compare' : 'Compare Mode'}
                </Button>
            </Box>

            <Typography sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.8rem',
                color: '#888',
                mb: 2,
                flexShrink: 0,
            }}>
                {compareMode
                    ? `Select 2 topologies to compare (${selectedTopologies.length}/2 selected)`
                    : 'Click a topology to expand details, then continue to simulation'}
            </Typography>

            {/* Compare Mode Selection Summary */}
            {compareMode && selectedTopologies.length > 0 && (
                <Box sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 2,
                    p: 1.5,
                    bgcolor: 'rgba(155, 89, 182, 0.1)',
                    borderRadius: 1,
                    border: `1px solid ${alpha(accentColor, 0.3)}`,
                }}>
                    {selectedTopologies.map(t => (
                        <Chip
                            key={t.id}
                            label={`#${t.id} ${t.name}`}
                            onDelete={() => handleTopologySelect(t)}
                            sx={{
                                bgcolor: alpha(getTopologyColor(t.id), 0.2),
                                color: getTopologyColor(t.id),
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.7rem',
                                '& .MuiChip-deleteIcon': {
                                    color: getTopologyColor(t.id),
                                    '&:hover': { color: '#fff' },
                                },
                            }}
                        />
                    ))}
                </Box>
            )}

            {/* Continue to Comparison Button */}
            {compareMode && selectedTopologies.length === 2 && (
                <Button
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => onContinueToSimulation(selectedTopologies)}
                    fullWidth
                    sx={{
                        mb: 2,
                        bgcolor: '#2ecc71',
                        color: '#000',
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        py: 1.5,
                        '&:hover': {
                            bgcolor: alpha('#2ecc71', 0.8),
                            boxShadow: `0 0 20px ${alpha('#2ecc71', 0.5)}`,
                        },
                    }}
                >
                    Compare These Topologies
                </Button>
            )}

            {/* Topology List - Fills remaining space */}
            <Box sx={{
                flex: 1,
                overflowY: 'auto',
                pr: 1,
                // Custom scrollbar
                '&::-webkit-scrollbar': {
                    width: 6,
                },
                '&::-webkit-scrollbar-track': {
                    bgcolor: 'rgba(255,255,255,0.05)',
                    borderRadius: 3,
                },
                '&::-webkit-scrollbar-thumb': {
                    bgcolor: alpha(accentColor, 0.3),
                    borderRadius: 3,
                    '&:hover': {
                        bgcolor: alpha(accentColor, 0.5),
                    },
                },
            }}>
                {topologies.map((topology) => (
                    <TopologyRow
                        key={topology.id}
                        topology={topology}
                        isExpanded={expandedId === topology.id}
                        isSelected={selectedTopologies.some(t => t.id === topology.id)}
                        onToggleExpand={handleToggleExpand}
                        onSelect={handleTopologySelect}
                        onContinue={onContinueToSimulation}
                        compareMode={compareMode}
                        selectedCount={selectedTopologies.length}
                    />
                ))}
            </Box>
        </Box>
    );
};

export default TopologyBrowser;
